import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import API_BASE_URL from '../../config/api';
import { variantFor, sheetNameFor } from '../../game/SpriteVariants';
import { SPRITE_SIZE } from '../../game/events';
import { ROW, ANIMATIONS } from '../../game/animations';

/**
 * Pick what your character looks like.
 *
 * OPTIONS COME FROM THE SERVER rather than a mirrored constant. The same module
 * that validates a submitted appearance is the one that says what is on offer,
 * so the picker cannot drift into showing a colour the server would reject.
 *
 * The preview is the real renderer — the same recoloured canvas the game draws
 * from, animated with the same frame table. A preview that approximated it
 * would be worse than none: you would be choosing against a lie.
 */

const PREVIEW_SCALE = 4;
const LABELS = {
  skin: 'Skin', hair: 'Hair colour', shirt: 'Shirt', pants: 'Trousers', shoes: 'Shoes',
};

/** Walk cycle, facing the viewer. */
const PREVIEW_ANIMATION = 'walk-down';

function Preview({ appearance }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return undefined;

    ctx.imageSmoothingEnabled = false;
    const size = SPRITE_SIZE;

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const sheet = variantFor(appearance);
      if (sheet) {
        const { frames, frameMs } = ANIMATIONS[PREVIEW_ANIMATION];
        const index = Math.floor(performance.now() / frameMs) % frames.length;
        const [col] = frames[index];
        ctx.drawImage(
          sheet,
          col * size,
          ROW.down * size,
          size,
          size,
          0,
          0,
          size * PREVIEW_SCALE,
          size * PREVIEW_SCALE,
        );
      }
      // Keep polling even before the sheet resolves — `variantFor` returns null
      // until the base image decodes, and this is what picks it up afterwards.
      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [appearance]);

  return (
    <canvas
      ref={canvasRef}
      width={SPRITE_SIZE * PREVIEW_SCALE}
      height={SPRITE_SIZE * PREVIEW_SCALE}
      style={{ imageRendering: 'pixelated' }}
      className="rounded-md bg-[#0D1B2A] border border-gray-700"
      aria-label="Character preview"
    />
  );
}

Preview.propTypes = { appearance: PropTypes.objectOf(PropTypes.string).isRequired };

function Swatches({
  slot, options, value, onChange,
}) {
  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {LABELS[slot] || slot}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((hex) => (
          <button
            key={hex}
            type="button"
            onClick={() => onChange(slot, hex)}
            aria-label={`${LABELS[slot] || slot} ${hex}`}
            aria-pressed={value === hex}
            style={{ backgroundColor: hex }}
            className={`w-8 h-8 rounded-md border-2 transition-transform ${
              value === hex
                ? 'border-primary scale-110'
                : 'border-gray-300 dark:border-gray-600 hover:scale-105'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

Swatches.propTypes = {
  slot: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
Swatches.defaultProps = { value: null };

function Choices({
  label, options, value, onChange,
}) {
  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={value === option}
            className={`px-3 py-1.5 rounded-md text-sm capitalize border transition-colors ${
              value === option
                ? 'bg-primary text-navy border-primary font-semibold'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-primary'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

Choices.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
Choices.defaultProps = { value: null };

function CharacterCustomizer({ appearance, onChange }) {
  const [options, setOptions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/auth/appearance-options`)
      .then((r) => r.json())
      .then((data) => { if (!cancelled) setOptions(data); })
      // A failed fetch leaves the pickers hidden rather than showing an empty
      // shell — the preview still renders, so the page is not broken.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setSlot = (slot, hex) => onChange({ ...appearance, [slot]: hex });

  return (
    <div className="flex flex-col sm:flex-row gap-6">
      <div className="flex flex-col items-center gap-2">
        <Preview appearance={appearance} />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {sheetNameFor(appearance)}
        </p>
      </div>

      {options && (
        <div className="flex-1 space-y-4">
          <Choices
            label="Body"
            options={options.bodies}
            value={appearance.body}
            onChange={(body) => onChange({ ...appearance, body })}
          />
          <Choices
            label="Hair style"
            options={options.hairStyles}
            value={appearance.hairStyle}
            onChange={(hairStyle) => onChange({ ...appearance, hairStyle })}
          />
          {Object.entries(options.palettes).map(([slot, swatches]) => (
            <Swatches
              key={slot}
              slot={slot}
              options={swatches}
              value={appearance[slot]}
              onChange={setSlot}
            />
          ))}
        </div>
      )}
    </div>
  );
}

CharacterCustomizer.propTypes = {
  appearance: PropTypes.objectOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CharacterCustomizer;
